import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { userApi, groupApi } from '../lib/api';
import { Plus, Trash2, Power, PowerOff, Pencil, Eye, KeyRound, ChevronLeft, ChevronRight, Search, X } from 'lucide-react';

interface CreateUserForm {
  email: string;
  givenName: string;
  familyName: string;
  role: string;
  tenantId: string;
  temporaryPassword: string;
  groups: string[];
}

interface EditUserForm {
  givenName: string;
  familyName: string;
  tenantId: string;
}

const emptyCreate: CreateUserForm = {
  email: '',
  givenName: '',
  familyName: '',
  role: 'ADMIN',
  tenantId: '',
  temporaryPassword: '',
  groups: [],
};

export default function Users() {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [paginationToken, setPaginationToken] = useState<string | undefined>();
  const [tokenHistory, setTokenHistory] = useState<(string | undefined)[]>([undefined]);
  const [currentPage, setCurrentPage] = useState(0);

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState<CreateUserForm>(emptyCreate);
  const [createError, setCreateError] = useState('');

  const [editUser, setEditUser] = useState<any | null>(null);
  const [editForm, setEditForm] = useState<EditUserForm>({ givenName: '', familyName: '', tenantId: '' });
  const [editError, setEditError] = useState('');

  const [resetTarget, setResetTarget] = useState<any | null>(null);

  const PAGE_SIZE = 20;

  const { data: allGroups } = useQuery({
    queryKey: ['groups'],
    queryFn: groupApi.list,
  });

  const { data: usersData, isLoading } = useQuery({
    queryKey: ['users', paginationToken, search],
    queryFn: () => userApi.list({
      limit: PAGE_SIZE,
      paginationToken,
      filter: search ? `email ^= "${search}"` : undefined,
    }),
  });

  const createMutation = useMutation({
    mutationFn: userApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowCreate(false);
      setCreateForm(emptyCreate);
      setCreateError('');
    },
    onError: (e: any) => setCreateError(e.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ username, data }: { username: string; data: any }) =>
      userApi.update(username, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setEditUser(null);
      setEditError('');
    },
    onError: (e: any) => setEditError(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: userApi.delete,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  });

  const enableMutation = useMutation({
    mutationFn: userApi.enable,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  });

  const disableMutation = useMutation({
    mutationFn: userApi.disable,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  });

  const resetMutation = useMutation({
    mutationFn: userApi.resetPassword,
    onSuccess: () => setResetTarget(null),
  });

  const openEdit = (user: any) => {
    setEditUser(user);
    setEditForm({ givenName: user.givenName, familyName: user.familyName, tenantId: user.tenantId || '' });
    setEditError('');
  };

  const handleNextPage = () => {
    if (!usersData?.paginationToken) return;
    const newHistory = [...tokenHistory, usersData.paginationToken];
    setTokenHistory(newHistory);
    setCurrentPage(currentPage + 1);
    setPaginationToken(usersData.paginationToken);
  };

  const handlePrevPage = () => {
    if (currentPage === 0) return;
    const newPage = currentPage - 1;
    setCurrentPage(newPage);
    setPaginationToken(tokenHistory[newPage]);
    setTokenHistory(tokenHistory.slice(0, newPage + 1));
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPaginationToken(undefined);
    setTokenHistory([undefined]);
    setCurrentPage(0);
  };

  const statusBadge = (user: any) => {
    const text = !user.enabled ? 'Inactivo' : user.status === 'FORCE_CHANGE_PASSWORD' ? 'Cambio de pass' : 'Activo';
    const color = !user.enabled
      ? 'bg-red-100 text-red-800'
      : user.status === 'FORCE_CHANGE_PASSWORD'
      ? 'bg-yellow-100 text-yellow-800'
      : 'bg-green-100 text-green-800';
    return <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${color}`}>{text}</span>;
  };

  return (
    <div className="space-y-6">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Usuarios</h1>
          <p className="mt-2 text-sm text-gray-700">Todos los usuarios del User Pool de Cognito.</p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            onClick={() => { setShowCreate(true); setCreateError(''); }}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nuevo usuario
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-xs">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Buscar por email..."
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="pl-9 pr-8 py-2 border border-gray-300 rounded-md text-sm w-full focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
        {search && (
          <button onClick={() => handleSearchChange('')} className="absolute right-2 top-2.5">
            <X className="h-4 w-4 text-gray-400" />
          </button>
        )}
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grupos</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr><td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500">Cargando...</td></tr>
              ) : usersData?.users?.length === 0 ? (
                <tr><td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500">No se encontraron usuarios</td></tr>
              ) : (
                usersData?.users?.map((user: any) => (
                  <tr key={user.userId} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {user.givenName} {user.familyName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.email}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <div className="flex flex-wrap gap-1">
                        {user.groups?.length > 0
                          ? user.groups.map((g: string) => (
                              <span key={g} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                                {g}
                              </span>
                            ))
                          : <span className="text-gray-400">—</span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{statusBadge(user)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <Link
                          to={`/users/${user.username}`}
                          className="text-blue-600 hover:text-blue-900"
                          title="Ver detalle"
                        >
                          <Eye className="h-4 w-4" />
                        </Link>
                        <button
                          onClick={() => openEdit(user)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Editar"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setResetTarget(user)}
                          className="text-orange-600 hover:text-orange-900"
                          title="Resetear contraseña"
                        >
                          <KeyRound className="h-4 w-4" />
                        </button>
                        {user.enabled ? (
                          <button
                            onClick={() => disableMutation.mutate(user.username)}
                            className="text-yellow-600 hover:text-yellow-900"
                            title="Deshabilitar"
                          >
                            <PowerOff className="h-4 w-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => enableMutation.mutate(user.username)}
                            className="text-green-600 hover:text-green-900"
                            title="Habilitar"
                          >
                            <Power className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => {
                            if (confirm(`¿Eliminar a ${user.email}?`)) {
                              deleteMutation.mutate(user.username);
                            }
                          }}
                          className="text-red-600 hover:text-red-900"
                          title="Eliminar"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
          <div className="text-sm text-gray-700">Página {currentPage + 1}</div>
          <div className="flex space-x-2">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 0}
              className="relative inline-flex items-center px-3 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 rounded-md"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={handleNextPage}
              disabled={!usersData?.paginationToken}
              className="relative inline-flex items-center px-3 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 rounded-md"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Create User Modal */}
      {showCreate && (
        <Modal title="Nuevo Usuario" onClose={() => setShowCreate(false)}>
          {createError && <p className="text-sm text-red-600 mb-3">{createError}</p>}
          <form onSubmit={(e) => {
            e.preventDefault();
            createMutation.mutate({
              email: createForm.email,
              givenName: createForm.givenName,
              familyName: createForm.familyName,
              role: createForm.role,
              tenantId: createForm.tenantId || undefined,
              temporaryPassword: createForm.temporaryPassword || undefined,
              groups: createForm.groups.length > 0 ? createForm.groups : undefined,
            });
          }} className="space-y-4">
            <Field label="Email *" id="c-email">
              <input id="c-email" type="email" required value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                className={inputCls} />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Nombre *" id="c-gn">
                <input id="c-gn" required value={createForm.givenName}
                  onChange={(e) => setCreateForm({ ...createForm, givenName: e.target.value })}
                  className={inputCls} />
              </Field>
              <Field label="Apellido *" id="c-fn">
                <input id="c-fn" required value={createForm.familyName}
                  onChange={(e) => setCreateForm({ ...createForm, familyName: e.target.value })}
                  className={inputCls} />
              </Field>
            </div>
            <Field label="TenantId" id="c-tid">
              <input id="c-tid" value={createForm.tenantId}
                onChange={(e) => setCreateForm({ ...createForm, tenantId: e.target.value })}
                className={inputCls} placeholder="Opcional" />
            </Field>
            <Field label="Contraseña temporal" id="c-pw">
              <input id="c-pw" type="password" value={createForm.temporaryPassword}
                onChange={(e) => setCreateForm({ ...createForm, temporaryPassword: e.target.value })}
                className={inputCls} placeholder="Se genera automáticamente si se deja vacío" />
            </Field>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Grupos</label>
              <p className="text-xs text-gray-400 mb-2">
                Sin grupos el usuario no puede acceder a ninguna aplicación.
              </p>
              <div className="space-y-1 max-h-36 overflow-y-auto border border-gray-200 rounded-md p-2">
                {(allGroups as any[] | undefined)?.map((g: any) => (
                  <label key={g.groupName} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={createForm.groups.includes(g.groupName)}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...createForm.groups, g.groupName]
                          : createForm.groups.filter(n => n !== g.groupName);
                        setCreateForm({ ...createForm, groups: next });
                      }}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600"
                    />
                    <span className="text-sm font-mono text-gray-700">{g.groupName}</span>
                  </label>
                ))}
                {!allGroups?.length && (
                  <p className="text-xs text-gray-400 p-1">Cargando grupos...</p>
                )}
              </div>
            </div>
            <div className="flex justify-end space-x-3 pt-2">
              <button type="button" onClick={() => setShowCreate(false)} className={btnSecondary}>Cancelar</button>
              <button type="submit" disabled={createMutation.isPending} className={btnPrimary}>
                {createMutation.isPending ? 'Creando...' : 'Crear usuario'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Edit User Modal */}
      {editUser && (
        <Modal title={`Editar: ${editUser.email}`} onClose={() => setEditUser(null)}>
          {editError && <p className="text-sm text-red-600 mb-3">{editError}</p>}
          <form onSubmit={(e) => {
            e.preventDefault();
            updateMutation.mutate({
              username: editUser.username,
              data: {
                givenName: editForm.givenName,
                familyName: editForm.familyName,
                tenantId: editForm.tenantId || undefined,
              },
            });
          }} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Nombre *" id="e-gn">
                <input id="e-gn" required value={editForm.givenName}
                  onChange={(e) => setEditForm({ ...editForm, givenName: e.target.value })}
                  className={inputCls} />
              </Field>
              <Field label="Apellido *" id="e-fn">
                <input id="e-fn" required value={editForm.familyName}
                  onChange={(e) => setEditForm({ ...editForm, familyName: e.target.value })}
                  className={inputCls} />
              </Field>
            </div>
            <Field label="TenantId" id="e-tid">
              <input id="e-tid" value={editForm.tenantId}
                onChange={(e) => setEditForm({ ...editForm, tenantId: e.target.value })}
                className={inputCls} />
            </Field>
            <div className="flex justify-end space-x-3 pt-2">
              <button type="button" onClick={() => setEditUser(null)} className={btnSecondary}>Cancelar</button>
              <button type="submit" disabled={updateMutation.isPending} className={btnPrimary}>
                {updateMutation.isPending ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Reset Password Confirm Modal */}
      {resetTarget && (
        <Modal title="Resetear contraseña" onClose={() => setResetTarget(null)}>
          <p className="text-sm text-gray-700 mb-4">
            Se enviará un email de reset de contraseña a <strong>{resetTarget.email}</strong>.
          </p>
          <div className="flex justify-end space-x-3">
            <button onClick={() => setResetTarget(null)} className={btnSecondary}>Cancelar</button>
            <button
              onClick={() => resetMutation.mutate(resetTarget.username)}
              disabled={resetMutation.isPending}
              className={btnPrimary}
            >
              {resetMutation.isPending ? 'Enviando...' : 'Confirmar'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, id, children }: { label: string; id: string; children: React.ReactNode }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
    </div>
  );
}

const inputCls = 'block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500';
const btnPrimary = 'inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50';
const btnSecondary = 'inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50';
